import { cineamoApiClient } from '../../apiClient';
import { buildUrlQuery, type UrlQueryParameter } from '../../helpers/url-query-helper';
import type { CinemaDto } from '../../models/cinema/CinemaDto.types';
import type {
    RoomAmbienceDto,
    RoomAmbienceInputDto,
    RoomAmbienceModelUploadCompleteDto,
    RoomAmbienceModelUploadUrlDto,
    RoomAmbienceModelUploadUrlInputDto,
    RoomAmbienceResolveDto
} from '../../models/room-ambience/RoomAmbienceDto.types';
import { type ApiErrorResponse, isApiErrorResponse } from '../../types/ApiErrorResponse.types';
import type { RoomAmbienceCatalogListResponse, RoomAmbienceListResponse } from '../../types/ApiListResponse.types';
import type { ApiResponse } from '../../types/ApiResponse.types';

/**
 * A rejection that is not an API error response (offline, CORS, DNS, a dropped connection: fetch-helper rethrows
 * the bare TypeError) must not look like a success DTO to a caller that only checks the shape. It becomes status 0.
 */
function toErrorResponse<TInputFields = undefined>(error: unknown): ApiErrorResponse<TInputFields> {
    return isApiErrorResponse(error)
        ? (error as ApiErrorResponse<TInputFields>)
        : { isError: true, status: 0, data: null };
}

// <------------------------ Room ambiences (admin) ------------------------>

export type GetRoomAmbiencesQuery = {
    sort?: 'id' | 'slug' | 'name' | 'validFrom' | 'priority' | 'updatedAt';
    isActive?: boolean;
    isMovieBound?: boolean;
    movieId?: number;
    /** `YYYY-MM-DD`: only rows that would match that date, ignoring the per-cinema opt-in. */
    activeOn?: string;
} & UrlQueryParameter;

/**
 * The list envelope. `isEnabled` sits beside `_embedded` and is the server kill switch (`ROOM_AMBIENCE_ENABLED`):
 * false means every cinema shows plain rooms, whatever is stored. `optedInCinemaCount` is the number of active cinemas
 * with the per-cinema opt-in on, so zero means no guest can see an ambience yet. Both absent from an older API.
 */
export type RoomAmbienceListEnvelope = ApiResponse<undefined, RoomAmbienceListResponse> & {
    isEnabled?: boolean;
    optedInCinemaCount?: number;
};

/**
 * Lists the stored ambiences. Admin only.
 *
 * @example
 * getRoomAmbiences({ query: 'snow', isActive: true, sort: 'priority', order: 'desc' });
 */
export async function getRoomAmbiences(params?: GetRoomAmbiencesQuery): Promise<RoomAmbienceListEnvelope | null> {
    return cineamoApiClient
        .get<RoomAmbienceListEnvelope>(`/room-ambiences${buildUrlQuery(params)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

export async function getRoomAmbienceById(
    roomAmbienceId: RoomAmbienceDto['id']
): Promise<ApiResponse<RoomAmbienceDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomAmbienceDto>>(`/room-ambiences/${roomAmbienceId}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * Creates an ambience. A 422 carries a JSON pointer per document violation ("/layers/3/count: max 2000"); a taken
 * slug is a 409. Both resolve to an error response so the form can show them.
 */
export async function createRoomAmbience(
    input: RoomAmbienceInputDto
): Promise<ApiResponse<RoomAmbienceDto> | ApiErrorResponse<RoomAmbienceInputDto>> {
    return cineamoApiClient
        .post<ApiResponse<RoomAmbienceDto>>('/room-ambiences', input)
        .then((response) => response.data)
        .catch((error) => toErrorResponse<RoomAmbienceInputDto>(error));
}

/** Only the fields sent change. Activating or deactivating is `{ isActive }`. The slug cannot be changed. */
export async function updateRoomAmbience(
    roomAmbienceId: RoomAmbienceDto['id'],
    input: Omit<RoomAmbienceInputDto, 'slug'>
): Promise<ApiResponse<RoomAmbienceDto> | ApiErrorResponse<RoomAmbienceInputDto>> {
    return cineamoApiClient
        .patch<ApiResponse<RoomAmbienceDto>>(`/room-ambiences/${roomAmbienceId}`, input)
        .then((response) => response.data)
        .catch((error) => toErrorResponse<RoomAmbienceInputDto>(error));
}

/** Resolves to `true` once deleted. */
export async function deleteRoomAmbience(roomAmbienceId: RoomAmbienceDto['id']): Promise<boolean | ApiErrorResponse> {
    return cineamoApiClient
        .delete(`/room-ambiences/${roomAmbienceId}`)
        .then((response) => response.status === 204)
        .catch(toErrorResponse);
}

// <------------------------ Catalog + resolve ------------------------>

export type GetRoomAmbienceCatalogQuery = Pick<UrlQueryParameter, 'page' | 'perPage' | 'query' | 'order'> & {
    sort?: GetRoomAmbiencesQuery['sort'];
};

/**
 * The active ambiences, without their documents: what the Studio offers to preview. Any cinema role that can edit
 * rooms. Resolves to null on error.
 */
export async function getRoomAmbienceCatalog(
    params?: GetRoomAmbienceCatalogQuery
): Promise<ApiResponse<undefined, RoomAmbienceCatalogListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<undefined, RoomAmbienceCatalogListResponse>>(`/room-ambiences/catalog${buildUrlQuery(params)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

export type GetCinemaRoomAmbienceQuery = {
    /** `YYYY-MM-DD`; defaults to today in the cinema's time zone. Within two years of today. */
    date?: string;
    /** Cineamo movie id (`showing._embedded.content.movieId`). */
    movieId?: number;
    /** Forces one ambience by slug. The public route honors it for active, non-movie-bound rows only. */
    ambience?: string;
};

/**
 * What the guest 3D seat map shows for a cinema, showing date and film. No authentication. The answer is
 * cacheable for 60 s. Resolves to null on any error: the room then renders plain.
 *
 * @example
 * getCinemaRoomAmbience(1, { date: '2026-10-31', movieId: 456 });
 */
export async function getCinemaRoomAmbience(
    cinemaId: CinemaDto['id'],
    params?: GetCinemaRoomAmbienceQuery,
    signal?: AbortSignal
): Promise<ApiResponse<RoomAmbienceResolveDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomAmbienceResolveDto>>(
            `/cinemas/${cinemaId}/room-ambience${buildUrlQuery(params as UrlQueryParameter | undefined)}`,
            // The route answers with `Access-Control-Allow-Origin: *`, which the browser refuses for a credentialed
            // request. The answer never depends on who asks, so no cookies and no auth headers go along (a Bearer
            // or movie-date header would force a CORS preflight, which this route's allowed_headers refuse).
            { signal, credentials: 'omit', anonymous: true }
        )
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * The same resolution for the operator's own cinema in the Studio: ignores the per-cinema opt-in and may force any
 * active ambience, movie-bound ones included. Not cacheable.
 */
export async function getCinemaRoomAmbiencePreview(
    cinemaId: CinemaDto['id'],
    params?: GetCinemaRoomAmbienceQuery,
    signal?: AbortSignal
): Promise<ApiResponse<RoomAmbienceResolveDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<RoomAmbienceResolveDto>>(
            `/cinemas/${cinemaId}/room-ambience/preview${buildUrlQuery(params as UrlQueryParameter | undefined)}`,
            { signal }
        )
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Model upload (admin) ------------------------>

/** Step one of a glTF upload: a presigned PUT target. At most 8 MiB. Admin only. */
export async function requestRoomAmbienceModelUploadUrl(
    input: RoomAmbienceModelUploadUrlInputDto
): Promise<ApiResponse<RoomAmbienceModelUploadUrlDto> | ApiErrorResponse> {
    return cineamoApiClient
        .post<ApiResponse<RoomAmbienceModelUploadUrlDto>>('/room-ambiences/model-upload-url', input)
        .then((response) => response.data)
        .catch(toErrorResponse);
}

/**
 * Step two, after the PUT: the API inspects the file and publishes it to the CDN. A refused file is a 422 naming
 * the violated rule.
 */
export async function completeRoomAmbienceModelUpload(
    ref: RoomAmbienceModelUploadUrlDto['ref']
): Promise<ApiResponse<RoomAmbienceModelUploadCompleteDto> | ApiErrorResponse> {
    return cineamoApiClient
        .post<ApiResponse<RoomAmbienceModelUploadCompleteDto>>('/room-ambiences/model-upload-complete', { ref })
        .then((response) => response.data)
        .catch(toErrorResponse);
}
